
import dayjs from 'dayjs';
import { useQuery } from 'react-query';

import type { IShowing } from 'src/api/landlord-showings-api';
import { apiClient } from 'src/services/api';
import { queryKeys } from 'src/services/api/constants';

const PATH = 'api/landlord/showings';

type RequestParams = {
    starts_before?: string;
    starts_after: string;
    select?: (data: IShowing[]) => any;
    /** When false the query is disabled (e.g. lazily-loaded past tours). */
    enabled?: boolean;
};

export const lastThirtyDays = (): string =>
    dayjs().subtract(30, 'day').startOf('day').format('YYYY-MM-DDTHH:mm:ss');

type Response = {
    showings: IShowing[];
    has_more: boolean;
};

const toNaiveUtc = (isoDateTime: string) => isoDateTime.replace(/(?:Z|[+-]\d{2}:\d{2})$/, '');

/**
 * Showings are returned by the API sorted by start time in descending order
 * (newest first, ties broken by id descending), and resolve in that order.
 *
 * `starts_after` (required) and `starts_before` (optional) bound the result by
 * date. The endpoint caps each call, so when `has_more` is set the hook keeps
 * fetching with the (starts_before, before_id) keyset cursor until the window
 * is exhausted, and resolves with the full window. The id tiebreaker keeps
 * pagination exact when multiple tours share a start time.
 *
 * The window is part of the query key, so distinct windows are cached
 * independently and navigating between them refetches only uncached windows.
 */
export const useShowings = (params: RequestParams) => {
    return useQuery(
        [
            queryKeys.showings,
            params.starts_after,
            params.starts_before ?? '',
        ],
        async () => {
            const showings: IShowing[] = [];
            let cursor = params.starts_before;
            let cursorId: number | undefined;

            for (;;) {
                const searchParams = new URLSearchParams();
                searchParams.append('whole_team', 'true');
                searchParams.append('starts_after', params.starts_after);
                if (cursor) {
                    searchParams.append('starts_before', cursor);
                }
                if (cursorId !== undefined) {
                    searchParams.append('before_id', String(cursorId));
                }

                const response = await apiClient(PATH, {
                    searchParams,
                }).json<Response>();

                showings.push(...response.showings);

                const oldest = response.showings[response.showings.length - 1];
                if (!response.has_more || !oldest) {
                    break;
                }
                cursor = toNaiveUtc(oldest.start_time);
                cursorId = oldest.id;
            }

            return showings;
        },
        {
            select: params.select,
            enabled: params.enabled ?? true,
        }
    );
};
